import { produce } from 'immer'
import { useCallback, useEffect } from 'react'
import type { RequireOneOrNone } from 'type-fest'
import { useTaskSubmitFormContext } from 'src/components/GenerationTask/BaseTaskSubmitForm'
import { useModelVersion } from 'src/stores/model'
import { canModelUseStyleMix, getRandomStyleMixRaw } from 'src/utils/model'
import { track } from 'src/utils/tracker'

export function useStyleMixBoosterState() {
  const { values, setValues } = useTaskSubmitFormContext()

  const { styleMix, modelId } = values

  const modelVersion = useModelVersion(modelId)
  const versionExtra = modelVersion?.extra
  const canStyleMix = canModelUseStyleMix(versionExtra)
  // Hide booster if styleMixSeed is a valid number, even if styleMix compatibility is explicitly enabled
  const hideStyleMixBooster =
    versionExtra?.styleMixSeed != null &&
    !Number.isNaN(Number(versionExtra.styleMixSeed))

  useEffect(() => {
    if (canStyleMix) return
    setValues(prev => {
      if (prev.styleMix == null) return prev
      return produce(prev, draft => {
        delete draft.styleMix
      })
    })
  }, [canStyleMix, setValues])

  const enabledStyleMix = !!styleMix?.enabled
  const toggleStyleMix = useCallback((enabled: boolean) => {
    track('stylemix_toggle', { checked: enabled })
    setValues(prev => ({
      ...prev,
      styleMix: {
        ...prev.styleMix,
        enabled,
        ...(enabled && typeof prev.styleMix?.seed !== 'number'
          ? { seed: getRandomStyleMixRaw() }
          : {}),
      },
    }))
  }, [])

  const shuffleStyleMix = useCallback(() => {
    setValues(prev => ({
      ...prev,
      styleMix: {
        ...prev.styleMix,
        seed: getRandomStyleMixRaw(),
      },
    }))
  }, [])

  const updateStyleMix = useCallback(
    (val: { seed: string; weight: number }) => {
      setValues(prev => ({
        ...prev,
        styleMix: {
          ...prev.styleMix,
          seed: Number(val.seed),
          weight: val.weight,
        },
      }))
    },
    [],
  )

  return [
    {
      enabled: enabledStyleMix,
      seed: styleMix?.seed,
      weight: styleMix?.weight,
    },
    toggleStyleMix,
    {
      shuffleStyleMix,
      updateStyleMix,
      canStyleMix,
      hideStyleMixBooster,
    },
  ] as const
}

const SEED_PREFIX = 'Pix'

/**
 * Returns a "seed" from 00001 to 99999
 */
export function getStyleMixSeed(
  opts?: {
    /** If provided, will be used instead of a random generated one */
    seed?: number
  } & RequireOneOrNone<
    {
      /**
       * Will padStart as well.
       * Can't enable both options
       */
      withPrefix?: boolean
      padStart?: boolean
    },
    'padStart' | 'withPrefix'
  >,
) {
  const seed = opts?.seed ?? getRandomStyleMixRaw()

  if (opts?.withPrefix) {
    return `${SEED_PREFIX}${seed.toString().padStart(5, '0')}`
  }

  if (opts?.padStart) {
    return seed.toString().padStart(5, '0')
  }

  return seed.toString()
}

export function padRandomStyleMixSeed(seed: number) {
  return seed.toString().padStart(5, '0')
}
